/**
 * ParadigmContext
 *
 * Provides paradigm-aware terminology throughout the application.
 * Switches between EA Travel (default) and Speaker Bureau modes
 * based on the organization's package setting.
 */

import React, { createContext, useContext, useMemo } from 'react';

// =============================================================================
// TERMINOLOGY MAPS
// =============================================================================

const PARADIGM_LABELS = {
  ea_travel: {
    // Core role terms
    executive: 'Executive',
    executives: 'Executives',
    anExecutive: 'an Executive',
    executiveAssistant: 'Executive Assistant',
    executiveAssistants: 'Executive Assistants',
    ea: 'EA',
    eas: 'EAs',

    // Actions & UI
    addExecutive: 'Add Executive',
    editExecutive: 'Edit Executive',
    deleteExecutive: 'Delete Executive',
    assignExecutives: 'Assign Executives',
    searchExecutive: 'Search Executive',
    selectExecutive: 'Select Executive',
    noExecutives: 'No executives yet',
    addAnExecutive: 'Add an executive to get started',

    // Page titles & sections
    executiveManagement: 'Manage Executives',
    executiveDashboard: 'Executive Dashboard',
    executiveProfile: 'Executive Profile',
    executiveDetails: 'Executive Details',
    executiveInformation: 'Executive Information',

    // Form fields
    executiveFirstName: "Executive's first name",
    executiveLastName: "Executive's last name",
    executiveEmail: 'executive@company.com',
    executiveOptional: 'Executive (optional)',

    // Email
    eaEmail: 'EA Email',
    eaEmails: 'EA Emails',
    eaInbox: 'EA Inbox',
    eaStandard: 'EA Standard',
    eaConfidential: 'EA Confidential',

    // Entitlements / seats
    executiveSeats: 'Executive Seats',
    eaSeats: 'EA Seats',
    perExecutive: 'per executive',
    dedicatedEmailsExecutives: 'Dedicated Emails for Executives',
    dedicatedEmailsEas: 'Dedicated Emails for EAs',

    // Misc
    appTagline: 'Executive travel, simplified',
    fillFromExecutive: 'Fill from Executive',
    executiveAccess: 'Executive Access',
    associateWithExecutive: 'Associate with Executive (Optional)',
    searchExecutivesByName: 'Type to search executives by name or email...',
  },

  speaker_bureau: {
    // Core role terms
    executive: 'Speaker',
    executives: 'Speakers',
    anExecutive: 'a Speaker',
    executiveAssistant: 'Meeting Planner',
    executiveAssistants: 'Meeting Planners',
    ea: 'Meeting Planner',
    eas: 'Meeting Planners',

    // Actions & UI
    addExecutive: 'Add Speaker',
    editExecutive: 'Edit Speaker',
    deleteExecutive: 'Delete Speaker',
    assignExecutives: 'Assign Speakers',
    searchExecutive: 'Search Speaker',
    selectExecutive: 'Select Speaker',
    noExecutives: 'No speakers yet',
    addAnExecutive: 'Add a speaker to get started',

    // Page titles & sections
    executiveManagement: 'Manage Speakers',
    executiveDashboard: 'Speaker Dashboard',
    executiveProfile: 'Speaker Profile',
    executiveDetails: 'Speaker Details',
    executiveInformation: 'Speaker Information',

    // Form fields
    executiveFirstName: "Speaker's first name",
    executiveLastName: "Speaker's last name",
    executiveEmail: 'speaker@company.com',
    executiveOptional: 'Speaker (optional)',

    // Email
    eaEmail: 'Planner Email',
    eaEmails: 'Planner Emails',
    eaInbox: 'Planner Inbox',
    eaStandard: 'Planner Standard',
    eaConfidential: 'Planner Confidential',

    // Entitlements / seats
    executiveSeats: 'Speaker Seats',
    eaSeats: 'Meeting Planner Seats',
    perExecutive: 'per speaker',
    dedicatedEmailsExecutives: 'Dedicated Emails for Speakers',
    dedicatedEmailsEas: 'Dedicated Emails for Meeting Planners',

    // Misc
    appTagline: 'Speaker travel, simplified',
    fillFromExecutive: 'Fill from Speaker',
    executiveAccess: 'Speaker Access',
    associateWithExecutive: 'Associate with Speaker (Optional)',
    searchExecutivesByName: 'Type to search speakers by name or email...',
  },
};

// =============================================================================
// CONTEXT
// =============================================================================

const ParadigmContext = createContext(null);

// =============================================================================
// PROVIDER
// =============================================================================

export function ParadigmProvider({ children, paradigm = 'ea_travel' }) {
  const value = useMemo(() => {
    const labels = PARADIGM_LABELS[paradigm] || PARADIGM_LABELS.ea_travel;

    // Maps internal role values to paradigm-aware display labels
    const getRoleLabel = (role) => {
      switch (role) {
        case 'ea': return labels.ea;
        case 'executive': return labels.executive;
        case 'superadmin': return 'Superadmin';
        case 'other': return 'Other';
        default: return role || '';
      }
    };

    return {
      paradigm,
      labels,
      getRoleLabel,
      isSpeakerBureau: paradigm === 'speaker_bureau',
      isEaTravel: paradigm === 'ea_travel' || !paradigm,
    };
  }, [paradigm]);

  return (
    <ParadigmContext.Provider value={value}>
      {children}
    </ParadigmContext.Provider>
  );
}

// =============================================================================
// HOOK
// =============================================================================

export function useParadigm() {
  const context = useContext(ParadigmContext);
  if (!context) {
    // Fallback for components rendered outside the provider
    const fallbackLabels = PARADIGM_LABELS.ea_travel;
    return {
      paradigm: 'ea_travel',
      labels: fallbackLabels,
      getRoleLabel: (role) => {
        switch (role) {
          case 'ea': return fallbackLabels.ea;
          case 'executive': return fallbackLabels.executive;
          case 'superadmin': return 'Superadmin';
          case 'other': return 'Other';
          default: return role || '';
        }
      },
      isSpeakerBureau: false,
      isEaTravel: true,
    };
  }
  return context;
}

export default ParadigmContext;
